import { midnightIsoParam as midnightIsoParam } from '@/shared/filters/dates';
import { msg } from '@lingui/core/macro';

import { defineFilters } from '@/shared/filters/define-filters';
import { stripDocumentSeparators } from '@/shared/format/internal';

import { centsParam } from './conciliation.filters';
import { HAS_CONCILIATION, hasConciliationParam, MOVEMENT_TYPES, ONLY_MANUAL, ONLY_WITH_BALANCE, onlyManualParam, onlyWithBalanceParam } from './suspense-account.enums';

// Filtros do Bolsão (components/DataTableFilters/SuspenseAccountFilters/**). Rótulos dos chips do legado
// (`availableFilters` dos contêineres) e a ordem do "Filtrar por".

/**
 * Os dois períodos do Bolsão têm `useDefaultFinalDateFormat: true` (SuspenseAccountListContainer.tsx:141-154;
 * SuspenseAccountDetailsContainer.tsx:121-127): início e fim saem como `new Date(<dia>).toISOString()`, a meia-noite
 * de São Paulo em UTC (`filterContext.tsx:92-99, 132-134`), não o início e o fim do dia com fuso.
 * Pendência de shared (P-S3): o mesmo helper existe em `wallet.filters.ts` (`midnightIso`).
 */
export { midnightIsoParam } from '@/shared/filters/dates';

/** Lista (SuspenseAccountFilters.tsx:39-56; SuspenseAccountListContainer.tsx:121-155). */
export const SUSPENSE_LIST_FILTERS = defineFilters([
  { key: 'operationNumber', type: 'text', label: msg`Nº da operação` },
  // A busca é exata e o backend guarda o documento sem máscara (SuspenseAccountFiltersContainer.tsx:32-35).
  { key: 'registrationNumber', type: 'document', label: msg`CPF/CNPJ`, toParam: stripDocumentSeparators },
  { key: 'clientName', type: 'text', label: msg`Cliente` },
  { key: 'corban', type: 'text', label: msg`Corban` },
  { key: 'assigneeFund', type: 'text', label: msg`Fundo cessionário` },
  { key: 'onlyWithBalance', type: 'select', label: msg`Saldo`, options: ONLY_WITH_BALANCE, toParam: onlyWithBalanceParam },
  // Campo de moeda do legado: o valor vai em centavos.
  { key: 'balanceMin', type: 'text', label: msg`Saldo mínimo`, placeholder: msg`R$ 0,00`, toParam: centsParam },
  { key: 'balanceMax', type: 'text', label: msg`Saldo máximo`, placeholder: msg`R$ 0,00`, toParam: centsParam },
  { key: 'hasConciliation', type: 'select', label: msg`Origem`, options: HAS_CONCILIATION, toParam: hasConciliationParam },
  {
    key: 'movementDateFrom',
    to: 'movementDateTo',
    type: 'dateRange',
    label: msg`Período do movimento`,
    fromLabel: msg`Movimento a partir de`,
    toLabel: msg`Movimento até`,
    fromParam: midnightIsoParam,
    toParam: midnightIsoParam,
  },
  {
    key: 'conciliationPeriodFrom',
    to: 'conciliationPeriodTo',
    type: 'dateRange',
    label: msg`Período da conciliação`,
    fromLabel: msg`Conciliação a partir de`,
    toLabel: msg`Conciliação até`,
    fromParam: midnightIsoParam,
    toParam: midnightIsoParam,
  },
] as const);

/** Extrato do detalhe (SuspenseAccountMovementsFilters.tsx:19-30; SuspenseAccountDetailsContainer.tsx:102-128). */
export const MOVEMENT_FILTERS = defineFilters([
  // `multiple: true` no legado: cada tipo escolhido repete a chave (`type=1&type=2`).
  { key: 'type', type: 'multiselect', label: msg`Tipo do movimento`, options: MOVEMENT_TYPES },
  {
    key: 'dateFrom',
    to: 'dateTo',
    type: 'dateRange',
    label: msg`Período do movimento`,
    fromLabel: msg`Movimento a partir de`,
    toLabel: msg`Movimento até`,
    fromParam: midnightIsoParam,
    toParam: midnightIsoParam,
  },
  { key: 'onlyManual', type: 'select', label: msg`Lançamento manual`, options: ONLY_MANUAL, toParam: onlyManualParam },
  { key: 'hasConciliation', type: 'select', label: msg`Origem`, options: HAS_CONCILIATION, toParam: hasConciliationParam },
] as const);
